import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../lib/api';

interface Entry {
  id: string;
  receivedAt: number;
  patternDetected: 'simple' | 'apikey' | 'jwt' | 'sso' | null;
  verificationOutcome: string;
  notes: string | null;
  signatureValid: boolean | null;
  signatureNotes: string | null;
  payloadJson: unknown;
}

export function WebhookLogPage() {
  const { data } = useQuery({
    queryKey: ['webhook-log'],
    queryFn: () => api.get<{ entries: Entry[] }>('/api/webhook-log'),
    refetchInterval: 5_000,
  });

  return (
    <div className="mx-auto max-w-5xl p-8">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Webhook log</h1>
        <Link to="/" className="text-sm underline">
          ← Home
        </Link>
      </header>
      <p className="mt-2 text-sm text-neutral-500">
        Every POST from Plaine is logged here with the verification result. Poll every 5s.
      </p>

      <ul className="mt-6 space-y-3">
        {data?.entries.map((e) => (
          <li key={e.id} className="rounded border border-neutral-200 p-4">
            <div className="flex items-center justify-between">
              <div className="font-mono text-xs text-neutral-500">
                {new Date(e.receivedAt).toISOString()}
              </div>
              <div className="flex gap-2">
                <div
                  className={
                    'rounded px-2 py-0.5 text-xs font-semibold ' +
                    (e.signatureValid === true
                      ? 'bg-emerald-100 text-emerald-900'
                      : e.signatureValid === false
                        ? 'bg-red-100 text-red-900'
                        : 'bg-neutral-100 text-neutral-600')
                  }
                  title={e.signatureNotes ?? ''}
                >
                  sig {e.signatureValid === true ? 'ok' : e.signatureValid === false ? 'fail' : '—'}
                </div>
                <div
                  className={
                    'rounded px-2 py-0.5 text-xs font-semibold ' +
                    (e.verificationOutcome === 'ok'
                      ? 'bg-emerald-100 text-emerald-900'
                      : 'bg-red-100 text-red-900')
                  }
                >
                  {e.verificationOutcome}
                </div>
              </div>
            </div>
            <div className="mt-1 text-sm">
              <span className="font-medium">{e.patternDetected ?? '—'}</span>
              {e.notes ? ` · ${e.notes}` : null}
            </div>
            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-neutral-500">payload</summary>
              <pre className="mt-2 overflow-x-auto rounded bg-neutral-900 p-3 text-xs text-neutral-100">
                {JSON.stringify(e.payloadJson, null, 2)}
              </pre>
            </details>
          </li>
        ))}
        {data?.entries.length === 0 ? (
          <li className="rounded border border-dashed border-neutral-300 p-6 text-center text-sm text-neutral-500">
            No webhooks yet. Start a conversation from the home page.
          </li>
        ) : null}
      </ul>
    </div>
  );
}
